import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useData } from '../../contexts/DataContext';
import StatusBadge from '../../components/ui/StatusBadge';
import CombinedCommentsWidget from '../../components/dashboard/CombinedCommentsWidget';
import CapacityBar from '../../components/ui/CapacityBar';
import { collection, getDocs, deleteDoc, doc } from 'firebase/firestore';
import { db, COLLECTIONS } from '../../firebase/config';
import DailyRoutinesWidget from '../../components/dashboard/DailyRoutinesWidget';
import SimpleTaskForm from '../../components/SimpleTaskForm';
import { Trash2 } from 'lucide-react';
import { 
  Users, 
  FolderOpen, 
  CheckSquare, 
  AlertTriangle,
  Clock,
  Calendar,
  X,
  Plus,
  FileText,
  Globe,
  User,
  Edit,
  AlertCircle,
  RotateCcw,
  Activity,
  Building2,
  Sun,
  Moon
} from 'lucide-react';
import TabsComponent from '../../components/ui/TabsComponent';
import { useNavigate } from 'react-router-dom';

import { renderTasksContent, renderProjectsContent, renderTeamsContent, renderDepartmentsContent } from '../../components/dashboard/InlineContent';

const TeamManagerDashboard = ({ activeTab, setActiveTab }) => {
  const { currentUser } = useAuth();
  const { tasks = [], projects = [], users = [], departments = [], teams = [],
          updateProject, updateTask, updateUser, createProject, createTask, deleteProject, deleteTask } = useData();
  const navigate = useNavigate();

  // State for filters
  const [filterProjectStatus, setFilterProjectStatus] = useState('all');
  const [filterTaskStatus, setFilterTaskStatus] = useState('all');
  const [filterTaskPriority, setFilterTaskPriority] = useState('all');
  const [filterProjectScope, setFilterProjectScope] = useState('all');
  const [showCompletedProjects, setShowCompletedProjects] = useState(false);
  const [showCompletedTasks, setShowCompletedTasks] = useState(false);
  
  // State for inline content display
  const [selectedContent, setSelectedContent] = useState(null); // 'tasks', 'projects', 'teams', 'departments'
  const [selectedTab, setSelectedTab] = useState('management'); // 'management', 'personal'
  useEffect(() => {
    setSelectedContent(null);
    setSelectedTab(activeTab);
  }, [activeTab]);

  // Function to handle box clicks
  const handleBoxClick = (contentType, tabType) => {
    setSelectedContent(contentType);
    setSelectedTab(tabType);
  };

  // Render inline content based on selection
  const renderInlineContent = () => {
    if (!selectedContent) return null;

    const isPersonal = selectedTab === 'personal';
    
    switch (selectedContent) {
      case 'tasks':
        return renderTasksContent(tasks, users, currentUser, isPersonal, setSelectedContent, navigate);
      case 'projects':
        return renderProjectsContent(projects, tasks, users, currentUser, isPersonal, setSelectedContent, navigate);
      case 'teams':
        return renderTeamsContent(teams, users, tasks, setSelectedContent, navigate);
      case 'departments':
        return renderDepartmentsContent(departments, users, tasks, setSelectedContent, navigate, currentUser);
      default:
        return null;
    }
  };

  // State for modals
  const [showEditProject, setShowEditProject] = useState(false);
  const [showEditTask, setShowEditTask] = useState(false);
  const [editProject, setEditProject] = useState(null);
  const [editTask, setEditTask] = useState(null);
  const [showNewProject, setShowNewProject] = useState(false);
  const [showNewTask, setShowNewTask] = useState(false);
  const [showAddTeamMemberModal, setShowAddTeamMemberModal] = useState(false);
  const [selectedUserToAdd, setSelectedUserToAdd] = useState('');
  const [showNewProjectModal, setShowNewProjectModal] = useState(false);
  const [showNewTaskModal, setShowNewTaskModal] = useState(false);
  
  // State for new forms
  const [newProject, setNewProject] = useState({
    name: '',
    description: '',
    scope: 'personal',
    priority: 'important_not_urgent',
    dueDate: '',
    dueTime: ''
  });
  
  const [newTask, setNewTask] = useState({
    title: '',
    description: '',
    priority: 'important_not_urgent',
    dueDate: '',
    dueTime: '',
    scope: 'personal',
    projectId: '',
    subtasks: []
  });

  // Helper functions
  const shouldHideTask = (task) => {
    // הסתר משימות שנוצרו משגרות יומיות - הן צריכות להופיע רק בעמוד השגרות
    if (task.isDailyRoutine || task.routineId || task.createdFrom === 'daily_routine') {
      return true;
    }
    
    // בדיקה נוספת - משימות שהשם שלהן מכיל תאריך (סימן למשימת שגרה)
    if (task.title && task.title.includes(' - ') && task.title.match(/\d{1,2}\/\d{1,2}\/\d{4}/)) {
      return true;
    }
    
    const taskProject = task.projectId ? projects.find(p => p.id === task.projectId) : null;
    const hidePersonalTask = task.scope === 'personal' && task.assigneeId !== currentUser?.id && task.createdBy !== currentUser?.id;
    const hidePersonalProjectTask = taskProject && taskProject.scope === 'personal' && taskProject.assigneeId !== currentUser?.id && taskProject.createdBy !== currentUser?.id;
    return hidePersonalTask || hidePersonalProjectTask;
  };

  const getProjectStatus = (project) => {
    if (!project) return 'לא ידוע';
    const projectTasks = tasks.filter(t => t.projectId === project.id);
    if (projectTasks.length === 0) return 'ממתין לעבודה';
    const completedTasks = projectTasks.filter(t => t.status === 'הושלם').length;
    const totalTasks = projectTasks.length;
    if (completedTasks === 0) return 'ממתין לעבודה';
    if (completedTasks === totalTasks) return 'פרויקט הושלם';
    return 'פרויקט בתהליך';
  };

  // Get team members - support multiple teams
  const userTeam = teams.find(t => t.managerId === currentUser.id);
  const teamMembers = userTeam ? users.filter(u => {
    if (u.id === currentUser.id) return false; // Don't include manager as member
    if (!u.isActive) return false;
    
    // Check new multi-team structure
    if (u.teamIds && Array.isArray(u.teamIds)) {
      return u.teamIds.includes(userTeam.id);
    }
    
    // Fallback to old single teamId for backward compatibility
    if (u.teamId) {
      return u.teamId === userTeam.id;
    }
    
    return false;
  }) : [];

  // Filter projects and tasks
  const filteredDashboardProjects = projects.filter(project => {
    let hasAccess = false;
    
    if (currentUser?.role === 'team_manager') {
      // מנהל צוות רואה פרויקטים שהצוות שלו מעורב בהם
      hasAccess = project.teamId === currentUser?.id || 
                  teamMembers.some(member => 
                    project.teamMembers && project.teamMembers.includes(member.id)
                  );
    }
    
    if (!hasAccess) return false;
    
    const matchesScope = filterProjectScope === 'all' || project.scope === filterProjectScope;
    const projectStatus = getProjectStatus(project);
    const matchesStatus = filterProjectStatus === 'all' || 
                         (filterProjectStatus === 'completed' && projectStatus === 'פרויקט הושלם') ||
                         projectStatus === filterProjectStatus;
    
    if (showCompletedProjects && projectStatus !== 'פרויקט הושלם') return false;
    if (!showCompletedProjects && projectStatus === 'פרויקט הושלם') return false;
    
    return matchesScope && matchesStatus;
  });

  const filteredDashboardTasks = tasks.filter(task => {
    if (shouldHideTask(task)) return false;
    
    // Show tasks assigned to team members
    if (currentUser?.role === 'team_manager') {
      if (task.scope === 'personal') {
        // Show only personal tasks of the user
        if (task.assigneeId !== currentUser?.id && task.createdBy !== currentUser?.id) {
          return false;
        }
      } else {
        // Show only organizational tasks assigned to team members or created by user
        const isTeamTask = teamMembers.some(member => 
          task.assigneeId === member.id || 
          (Array.isArray(task.assigneeIds) && task.assigneeIds.includes(member.id))
        );
        if (!isTeamTask && task.assigneeId !== currentUser?.id && task.createdBy !== currentUser?.id) {
          return false;
        }
      }
    }
    
    const matchesStatus = filterTaskStatus === 'all' || task.status === filterTaskStatus;
    const matchesPriority = filterTaskPriority === 'all' || task.priority === filterTaskPriority;
    if (showCompletedTasks && task.status !== 'הושלם') return false;
    if (!showCompletedTasks && task.status === 'הושלם') return false;
    return matchesStatus && matchesPriority;
  });

  // Stats calculations
  const teamTasks = filteredDashboardTasks.filter(t => 
    teamMembers.some(member => 
      t.assigneeId === member.id || 
      (Array.isArray(t.assigneeIds) && t.assigneeIds.includes(member.id))
    )
  );

  const unacknowledgedTasks = teamTasks.filter(t => 
    !t.acknowledged && 
    teamMembers.some(m => 
      t.assigneeId === m.id || 
      (Array.isArray(t.assigneeIds) && t.assigneeIds.includes(m.id))
    )
  );

  const atRiskTasks = teamTasks.filter(t => 
    t.status === 'בסיכון' || t.status === 'חסום'
  );

  // Stats card component
  const StatCard = ({ title, value, icon: Icon, color = 'blue' }) => {
    const colorClasses = {
      blue: 'bg-blue-50 text-blue-600',
      green: 'bg-green-50 text-green-600',
      yellow: 'bg-yellow-50 text-yellow-600',
      red: 'bg-red-50 text-red-600'
    };

    return (
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-gray-600">{title}</p>
            <p className="text-2xl font-bold mt-1">{value || 0}</p>
          </div>
          <div className={`p-3 rounded-lg ${colorClasses[color]}`}>
            <Icon size={24} />
          </div>
        </div>
      </div>
    );
  };

  // Function to add team member
  const handleAddTeamMember = async () => {
    if (!selectedUserToAdd) return;
    
    try {
      const selectedUser = users.find(u => u.id === selectedUserToAdd);
      if (selectedUser?.teamId) {
        alert('העובד כבר משויך לצוות אחר!');
        return;
      }
      
      await updateUser(selectedUserToAdd, { 
        teamId: currentUser.id,
        teamManagerId: currentUser.id 
      });
      
      alert('העובד נוסף לצוות בהצלחה!');
      
      setShowAddTeamMemberModal(false);
      setSelectedUserToAdd('');
      
    } catch (error) {
      console.error('Error adding team member:', error);
      alert('שגיאה בהוספת העובד לצוות');
    }
  };

  // Function to create new project
  const handleCreateProject = async (e) => {
    e.preventDefault();
    try {
 const projectData = {
  ...newProject,
  teamId: userTeam?.id,
  departmentId: currentUser?.departmentId,
  createdBy: currentUser.id,
  creatorId: currentUser.id, // חדש
  createdAt: new Date(),
  status: 'active',
  // השינוי החשוב - בודק באיזה טאב אנחנו
  scope: activeTab === 'management' ? 'managerial' : 'personal'
};
      
      await createProject(projectData);
      setShowNewProjectModal(false);
      setNewProject({
        name: '',
        description: '',
        scope: 'personal',
        priority: 'important_not_urgent',
        dueDate: '',
        dueTime: ''
      });
      alert('הפרויקט נוצר בהצלחה!');
    } catch (error) {
      console.error('Error creating project:', error);
      alert('שגיאה ביצירת הפרויקט');
    }
  };

  // Function to create new task
  const handleCreateTask = async (e) => {
    e.preventDefault();
    try {
    const taskData = {
  ...newTask,
  assigneeId: currentUser.id,
  assigneeIds: [currentUser.id], // חדש - מוסיף מערך של מוקצים
  teamId: userTeam?.id,
  departmentId: currentUser?.departmentId,
  createdBy: currentUser.id,
  creatorId: currentUser.id, // חדש - מוסיף ID של היוצר
  createdAt: new Date(),
  status: 'במסלול',
  // השינוי החשוב - בודק באיזה טאב אנחנו
  scope: activeTab === 'management' ? 'managerial' : 'personal',
  subtasks: newTask.subtasks ? newTask.subtasks.map(st => st.text).filter(text => text) : []
};
      
      await createTask(taskData);
      setShowNewTaskModal(false);
      setNewTask({
        title: '',
        description: '',
        priority: 'important_not_urgent',
        dueDate: '',
        dueTime: '',
        scope: 'personal',
        projectId: '',
        subtasks: []
      });
      alert('המשימה נוצרה בהצלחה!');
    } catch (error) {
      console.error('Error creating task:', error);
      alert('שגיאה ביצירת המשימה');
    }
  };

  // Function to add subtask
  const addSubtask = () => {
    setNewTask({
      ...newTask,
      subtasks: [...newTask.subtasks, { id: Date.now(), text: '', completed: false }]
    });
  };

  // Function to update subtask
  const updateSubtask = (id, text) => {
    setNewTask({
      ...newTask,
      subtasks: newTask.subtasks.map(st => 
        st.id === id ? { ...st, text } : st
      )
    });
  };

  // Function to remove subtask
  const removeSubtask = (id) => {
    setNewTask({
      ...newTask,
      subtasks: newTask.subtasks.filter(st => st.id !== id)
    });
  };

  // Get team projects for task form
  const getTeamProjects = () => {
    if (selectedTab === 'personal') {
      return projects.filter(p => 
        p.scope === 'personal' && 
        (p.assigneeId === currentUser.id || p.createdBy === currentUser.id)
      );
    } else {
      return projects.filter(p => {
        if (p.scope === 'team' && p.teamId === userTeam?.id) return true;
        if (p.teamMembers && teamMembers.some(m => p.teamMembers.includes(m.id))) return true;
        return false;
      });
    }
  };

  // Tabs configuration
  const tabs = (() => {
    const managementTab = {
      id: 'management',
      label: 'ניהול',
      icon: Users,
      badge: teamMembers.length.toString(),
      content: (
        <div className="bg-white rounded-lg shadow">
          <div className="p-6 border-b">
            <div className="text-center mb-4">
              <h2 className="text-2xl font-bold mb-3">ניהול הצוות</h2>
              <div className="inline-block px-6 py-3 bg-purple-100 border-2 border-purple-300 rounded-xl mb-3">
                <div className="flex items-center gap-3">
                  <Users size={24} className="text-purple-700" />
                  <span className="text-xl font-bold text-purple-900">
                    מחלקה: {(() => {
                      const userDepartment = departments.find(d => d.id === currentUser?.departmentId);
                      return userDepartment ? userDepartment.name : 'לא משויך למחלקה';
                    })()}
                  </span>
                </div>
              </div>
              <p className="text-sm text-gray-600">בחר קטגוריה כדי לראות את המידע הרלוונטי</p>
            </div>
          </div>
          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {/* Teams Card */}
              <div className="border rounded-lg p-6 hover:bg-gray-50 cursor-pointer" onClick={() => navigate('/teams')}>
                <div className="text-center">
                  <div className="mx-auto w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mb-4">
                    <Users size={32} className="text-blue-600" />
                  </div>
                  <h3 className="text-lg font-semibold mb-2">הצוות שלי</h3>
                  <p className="text-3xl font-bold text-blue-600 mb-2">1</p>
                  <p className="text-sm text-gray-600">צוות פעיל</p>
                  <div className="mt-4 text-xs text-gray-500">
                    <div className="text-center py-1">
                      {teamMembers.length} חברי צוות
                    </div>
                    <div className="text-center py-1 text-blue-600">
                      {teamMembers.filter(m => m.isActive).length} פעילים
                    </div>
                    {/* Department Info */}
                    <div className="text-center py-1 text-purple-600 border-t border-gray-200 mt-2 pt-2">
                      <strong>מחלקה:</strong> {(() => {
                        const userDepartment = departments.find(d => d.id === currentUser?.departmentId);
                        return userDepartment ? userDepartment.name : 'לא משויך למחלקה';
                      })()}
                    </div>
                  </div>
                </div>
              </div>

              {/* Projects Card */}
              <div className="border rounded-lg p-6 hover:bg-gray-50 cursor-pointer" onClick={() => handleBoxClick('projects', 'management')}>
                <div className="text-center">
                  <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
                    <FolderOpen size={32} className="text-green-600" />
                  </div>
                  <h3 className="text-lg font-semibold mb-2">פרויקטי הצוות</h3>
                  <p className="text-3xl font-bold text-green-600 mb-2">{filteredDashboardProjects.length}</p>
                  <p className="text-sm text-gray-600">פרויקטים פעילים</p>
                  <div className="mt-4 text-xs text-gray-500">
                    {(() => {
                      const activeProjects = filteredDashboardProjects.filter(p => {
                        const pts = tasks.filter(t => t.projectId === p.id);
                        const done = pts.filter(t => t.status === 'הושלם').length;
                        return pts.length === 0 || done < pts.length;
                      });
                      const completedProjects = filteredDashboardProjects.filter(p => {
                        const pts = tasks.filter(t => t.projectId === p.id);
                        const done = pts.filter(t => t.status === 'הושלם').length;
                        return pts.length > 0 && done === pts.length;
                      });
                      return (
                        <>
                          <div className="text-center py-1">
                            {activeProjects.length} פעילים
                          </div>
                          <div className="text-center py-1 text-green-600">
                            {completedProjects.length} הושלמו
                          </div>
                        </>
                      );
                    })()}
                  </div>
                </div>
              </div>

              {/* Tasks Card */}
              <div className="border rounded-lg p-6 hover:bg-gray-50 cursor-pointer" onClick={() => handleBoxClick('tasks', 'management')}>
                <div className="text-center">
                  <div className="mx-auto w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mb-4">
                    <CheckSquare size={32} className="text-purple-600" />
                  </div>
                  <h3 className="text-lg font-semibold mb-2">משימות הצוות</h3>
                  <p className="text-3xl font-bold text-purple-600 mb-2">{teamTasks.length}</p>
                  <p className="text-sm text-gray-600">משימות פעילות</p>
                  <div className="mt-4 text-xs text-gray-500">
                    {(() => {
                      const activeTasks = teamTasks.filter(t => t.status !== 'הושלם');
                      const completedTasks = teamTasks.filter(t => t.status === 'הושלם');
                      return (
                        <>
                          <div className="text-center py-1">
                            {activeTasks.length} פעילות
                          </div>
                          <div className="text-center py-1 text-purple-600">
                            {completedTasks.length} הושלמו
                          </div>
                        </>
                      );
                    })()}
                  </div>
                </div>
              </div>

              {/* Team Members Card */}
              <div className="border rounded-lg p-6 hover:bg-gray-50 cursor-pointer" onClick={() => setShowAddTeamMemberModal(true)}>
                <div className="text-center">
                  <div className="mx-auto w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mb-4">
                    <User size={32} className="text-orange-600" />
                  </div>
                  <h3 className="text-lg font-semibold mb-2">חברי צוות</h3>
                  <p className="text-3xl font-bold text-orange-600 mb-2">{teamMembers.length}</p>
                  <p className="text-sm text-gray-600">עובדים פעילים</p>
                  <div className="mt-4 text-xs text-gray-500">
                    <div className="text-center py-1">
                      {teamMembers.filter(m => m.isActive).length} פעילים
                    </div>
                    <div className="text-center py-1 text-orange-600">
                      {teamMembers.filter(m => !m.isActive).length} לא פעילים
                    </div>
                  </div>
                </div>
              </div>
            </div>
            {/* תוכן מפורט שנפתח כשלוחצים על כרטיסייה */}
            {renderInlineContent()}
            {/* Quick Actions */}
            <div className="mt-8 pt-6 border-t">
              <h3 className="text-lg font-medium mb-4">פעולות מהירות</h3>
              <div className="flex flex-wrap gap-3">
                <button 
                  onClick={() => setShowAddTeamMemberModal(true)}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
                >
                  <Users size={16} />
                  הוסף עובד לצוות
                </button>
                <button 
                 onClick={() => setShowNewProjectModal(true)}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center gap-2"
                >
                  <FolderOpen size={16} />
                  פרויקט חדש
                </button>
                <button 
                  onClick={() => setShowNewTaskModal(true)}
                  className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 flex items-center gap-2"
                >
                  <CheckSquare size={16} />
                  משימה חדשה
                </button>
              </div>
            </div>
            {/* Daily Routines Section */}
            <DailyRoutinesWidget />
          </div>
          {/* הערות חדשות */}
          <CombinedCommentsWidget /> 

          {/* Day Opening & Summary Section */}
          <div className="bg-white rounded-lg shadow p-6 mb-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-gradient-to-r from-orange-500 to-purple-500 rounded-lg flex items-center justify-center">
                  <Sun className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold">פתיחה וסיכום יום</h2>
                  <p className="text-sm text-gray-600">נהל את היום שלך בצורה מסודרת</p>
                </div>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => navigate('/day-opening')}
                  className="px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 flex items-center gap-2 text-sm font-medium"
                >
                  <Sun className="w-4 h-4" />
                  פתיחת יום
                </button>
                <button
                  onClick={() => navigate('/day-summary')}
                  className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 flex items-center gap-2 text-sm font-medium"
                >
                  <Moon className="w-4 h-4" />
                  סיכום יום
                </button>
                <button
                  onClick={() => navigate('/team-summaries')}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2 text-sm font-medium"
                >
                  <Users className="w-4 h-4" />
                  סיכומי הצוות
                </button>
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Sun className="w-5 h-5 text-orange-600" />
                  <h3 className="font-semibold text-orange-800">פתיחת יום</h3>
                </div>
                <p className="text-sm text-orange-700">
                  תכנן את היום: יעדים, משימות מרכזיות, פגישות וחסמים צפויים
                </p>
              </div>
              
              <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Moon className="w-5 h-5 text-purple-600" />
                  <h3 className="font-semibold text-purple-800">סיכום יום</h3>
                </div>
                <p className="text-sm text-purple-700">
                  סכם את היום: הישגים, אתגרים, תובנות ותכנון למחר
                </p>
              </div>
              
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Users className="w-5 h-5 text-blue-600" />
                  <h3 className="font-semibold text-blue-800">סיכומי הצוות</h3>
                </div>
                <p className="text-sm text-blue-700">
                  צפה בפתיחות וסיכומי יום של חברי הצוות שלך
                </p>
              </div>
            </div>
          </div>
        </div>
      )
    };

    const personalTab = {
      id: 'personal',
      label: 'אישי',
      icon: User,
      badge: (() => {
        const personalProjects = projects.filter(p => 
            p.scope === 'personal' && 
            (p.assigneeId === currentUser.id || p.createdBy === currentUser.id)
        );
        const personalTasks = tasks.filter(t => 
            t.scope === 'personal' && 
            (t.assigneeId === currentUser.id || t.createdBy === currentUser.id)
        );
        return (personalProjects.length + personalTasks.length).toString();
      })(),
      content: (
        <div className="p-6">
          <div className="text-center mb-6">
            <h1 className="text-3xl font-bold mb-2">לוח בקרה - מנהל צוות</h1>
            <div className="inline-block px-6 py-3 bg-purple-100 border-2 border-purple-300 rounded-xl">
              <div className="flex items-center gap-3">
                <Users size={28} className="text-purple-700" />
                <span className="text-2xl font-bold text-purple-900">
                  מחלקה: {(() => {
                    const userDepartment = departments.find(d => d.id === currentUser?.departmentId);
                    return userDepartment ? userDepartment.name : 'לא משויך למחלקה';
                  })()}
                </span>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-lg shadow p-6">
            <div className="border-b pb-4 mb-4">
              <h2 className="text-lg font-semibold">פרויקטים ומשימות אישיים</h2>
              <p className="text-sm text-gray-600 mt-1">ניהול הפרויקטים והמשימות האישיים שלך</p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Personal Projects Card */}
              <div className="border rounded-lg p-6 hover:bg-gray-50 cursor-pointer" onClick={() => handleBoxClick('projects', 'personal')}>
                <div className="text-center">
                  <div className="mx-auto w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mb-4">
                    <FolderOpen size={32} className="text-blue-600" />
                  </div>
                  <h3 className="text-lg font-semibold mb-2">פרויקטים אישיים</h3>
                  <p className="text-3xl font-bold text-blue-600 mb-2">
                    {(() => {
                      const personalProjects = projects.filter(p => p.scope === 'personal');
                      return personalProjects.length;
                    })()}
                  </p>
                  <p className="text-sm text-gray-600">פרויקטים אישיים</p>
                  <div className="mt-4 text-xs text-gray-500">
                    {(() => {
                      const personalProjects = projects.filter(p => p.scope === 'personal');
                      const activeProjects = personalProjects.filter(p => {
                        const pts = tasks.filter(t => t.projectId === p.id);
                        const done = pts.filter(t => t.status === 'הושלם').length;
                        return pts.length === 0 || done < pts.length;
                      });
                      const completedProjects = personalProjects.filter(p => {
                        const pts = tasks.filter(t => t.projectId === p.id);
                        const done = pts.filter(t => t.status === 'הושלם').length;
                        return pts.length > 0 && done === pts.length;
                      });
                      return (
                        <>
                          <div className="text-center py-1">
                            {activeProjects.length} פעילים
                          </div>
                          <div className="text-center py-1 text-blue-600">
                            {completedProjects.length} הושלמו
                          </div>
                        </>
                      );
                    })()}
                  </div>
                </div>
              </div>

              {/* Personal Tasks Card */}
              <div className="border rounded-lg p-6 hover:bg-gray-50 cursor-pointer" onClick={() => handleBoxClick('tasks', 'personal')}>
                <div className="text-center">
                  <div className="mx-auto w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mb-4">
                    <CheckSquare size={32} className="text-purple-600" />
                  </div>
                  <h3 className="text-lg font-semibold mb-2">משימות אישיות</h3>
                  <p className="text-3xl font-bold text-purple-600 mb-2">
                    {(() => {
                      const personalTasks = tasks.filter(t => 
                        t.scope === 'personal' && 
                        (t.assigneeId === currentUser.id || t.createdBy === currentUser.id)
                      );
                      return personalTasks.length;
                    })()}
                  </p>
                  <p className="text-sm text-gray-600">משימות אישיות</p>
                  <div className="mt-4 text-xs text-gray-500">
                    {(() => {
                      const personalTasks = tasks.filter(t => t.scope === 'personal');
                      const activeTasks = personalTasks.filter(t => t.status !== 'הושלם');
                      const completedTasks = personalTasks.filter(t => t.status === 'הושלם');
                      return (
                        <>
                          <div className="text-center py-1">
                            {activeTasks.length} פעילות
                          </div>
                          <div className="text-center py-1 text-purple-600">
                            {completedTasks.length} הושלמו
                          </div>
                        </>
                      );
                    })()}
                  </div>
                </div>
              </div>
            </div>
            {/* תוכן מפורט שנפתח כשלוחצים על כרטיסייה */}
            {renderInlineContent()}
            {/* Quick Actions */}
            <div className="mt-8 pt-6 border-t">
              <h3 className="text-lg font-medium mb-4">פעולות מהירות</h3>
              <div className="flex flex-wrap gap-3">
                <button 
                  onClick={() => setShowNewProjectModal(true)}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
                >
                  <FolderOpen size={16} />
                  פרויקט חדש
                </button>
                <button 
                  onClick={() => setShowNewTaskModal(true)}
                  className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 flex items-center gap-2"
                >
                  <CheckSquare size={16} />
                  משימה חדשה
                </button>
              </div>
            </div>
          </div>
        </div>
      )
    };

    return [managementTab, personalTab];
  })();

  return (
    <div className="p-6">
      <TabsComponent tabs={tabs} activeTab={activeTab} onTabChange={setActiveTab} />
      
      {/* Add Team Member Modal */}
      {showAddTeamMemberModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
            <div className="p-6">
              <h2 className="text-xl font-bold mb-4">הוסף עובד לצוות</h2>
              <select
                value={selectedUserToAdd}
                onChange={(e) => setSelectedUserToAdd(e.target.value)}
                className="w-full p-2 border rounded mb-4"
              >
                <option value="">בחר עובד</option>
                {users.filter(u => !u.teamId && u.isActive && u.id !== currentUser.id).map(user => (
                  <option key={user.id} value={user.id}>
                    {user.fullName} - {user.role}
                  </option>
                ))}
              </select>
              <div className="flex justify-end gap-3">
                <button
                  onClick={() => {
                    setShowAddTeamMemberModal(false);
                    setSelectedUserToAdd('');
                  }}
                  className="px-4 py-2 bg-gray-200 rounded hover:bg-gray-300"
                >
                  ביטול
                </button>
                <button
                  onClick={handleAddTeamMember}
                  disabled={!selectedUserToAdd}
                  className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
                >
                  הוסף לצוות
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* New Project Modal */}
      {showNewProjectModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl mx-4">
            <div className="bg-blue-600 p-4 text-white">
              <h2 className="text-xl font-bold">פרויקט חדש</h2>
            </div>
            <form onSubmit={handleCreateProject} className="p-6">
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-1">שם הפרויקט</label>
                  <input
                    type="text"
                    value={newProject.name}
                    onChange={(e) => setNewProject({...newProject, name: e.target.value})}
                    className="w-full p-2 border rounded"
                    required
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium mb-1">תיאור הפרויקט</label>
                  <textarea
                    value={newProject.description}
                    onChange={(e) => setNewProject({...newProject, description: e.target.value})}
                    className="w-full p-2 border rounded"
                    rows="3"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium mb-1">תחום</label>
                  <select
                    value={newProject.scope}
                    onChange={(e) => setNewProject({...newProject, scope: e.target.value})}
                    className="w-full p-2 border rounded"
                  >
                    <option value="personal">אישי</option>
                    <option value="team">צוותי</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium mb-1">עדיפות</label>
                  <select
                    value={newProject.priority}
                    onChange={(e) => setNewProject({...newProject, priority: e.target.value})}
                    className="w-full p-2 border rounded"
                  >
                    <option value="urgent_important">חשוב דחוף</option>
                    <option value="important_not_urgent">חשוב לא דחוף</option>
                    <option value="urgent_not_important">דחוף לא חשוב</option>
                    <option value="not_urgent_not_important">לא חשוב לא דחוף</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium mb-1">תאריך יעד</label>
                  <input
                    type="date"
                    value={newProject.dueDate}
                    onChange={(e) => setNewProject({...newProject, dueDate: e.target.value})}
                    className="w-full p-2 border rounded"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium mb-1">שעת יעד</label>
                  <input
                    type="time"
                    value={newProject.dueTime}
                    onChange={(e) => setNewProject({...newProject, dueTime: e.target.value})}
                    className="w-full p-2 border rounded"
                  />
                </div>
              </div>
              
              <div className="mt-6 flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => setShowNewProjectModal(false)}
                  className="px-4 py-2 bg-gray-200 rounded hover:bg-gray-300"
                >
                  ביטול
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                >
                  צור פרויקט
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* New Task Modal */}
      {showNewTaskModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
            <div className="bg-purple-600 p-4 text-white sticky top-0">
              <h2 className="text-xl font-bold">משימה חדשה</h2>
            </div>
            <form onSubmit={handleCreateTask} className="p-6">
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-1">כותרת המשימה</label>
                  <input
                    type="text"
                    value={newTask.title}
                    onChange={(e) => setNewTask({...newTask, title: e.target.value})}
                    className="w-full p-2 border rounded"
                    required
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium mb-1">תיאור המשימה</label>
                  <textarea
                    value={newTask.description}
                    onChange={(e) => setNewTask({...newTask, description: e.target.value})}
                    className="w-full p-2 border rounded"
                    rows="3"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium mb-1">פרויקט</label>
                  <select
                    value={newTask.projectId}
                    onChange={(e) => setNewTask({...newTask, projectId: e.target.value})}
                    className="w-full p-2 border rounded"
                  >
                    <option value="">ללא פרויקט</option>
                    {getTeamProjects().map(project => (
                      <option key={project.id} value={project.id}>
                        {project.name}
                      </option>
                    ))}
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium mb-1">תחום</label>
                  <select
                    value={newTask.scope}
                    onChange={(e) => setNewTask({...newTask, scope: e.target.value})}
                    className="w-full p-2 border rounded"
                  >
                    <option value="personal">אישי</option>
                    <option value="team">צוותי</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium mb-1">עדיפות</label>
                  <select
                    value={newTask.priority}
                    onChange={(e) => setNewTask({...newTask, priority: e.target.value})}
                    className="w-full p-2 border rounded"
                  >
                    <option value="urgent_important">חשוב דחוף</option>
                    <option value="important_not_urgent">חשוב לא דחוף</option>
                    <option value="urgent_not_important">דחוף לא חשוב</option>
                    <option value="not_urgent_not_important">לא חשוב לא דחוף</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium mb-1">תאריך יעד</label>
                  <input
                    type="date"
                    value={newTask.dueDate}
                    onChange={(e) => setNewTask({...newTask, dueDate: e.target.value})}
                    className="w-full p-2 border rounded"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium mb-1">שעת יעד</label>
                  <input
                    type="time"
                    value={newTask.dueTime}
                    onChange={(e) => setNewTask({...newTask, dueTime: e.target.value})}
                    className="w-full p-2 border rounded"
                  />
                </div>
                
                {/* Subtasks Section */}
                <div className="border-t pt-4">
                  <div className="flex justify-between items-center mb-3">
                    <label className="text-sm font-medium">תתי משימות</label>
                    <button
                      type="button"
                      onClick={addSubtask}
                      className="px-3 py-1 bg-green-500 text-white rounded text-sm hover:bg-green-600 flex items-center gap-1"
                    >
                      <Plus size={16} />
                      הוסף תת משימה
                    </button>
                  </div>
                  
                  {newTask.subtasks.length > 0 && (
                    <div className="space-y-2">
                      {newTask.subtasks.map((subtask, index) => (
                        <div key={subtask.id} className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            checked={subtask.completed}
                            onChange={(e) => {
                              const updatedSubtasks = newTask.subtasks.map(st => 
                                st.id === subtask.id ? { ...st, completed: e.target.checked } : st
                              );
                              setNewTask({ ...newTask, subtasks: updatedSubtasks });
                            }}
                            className="rounded"
                          />
                          <input
                            type="text"
                            value={subtask.text}
                            onChange={(e) => updateSubtask(subtask.id, e.target.value)}
                            placeholder={`תת משימה ${index + 1}`}
                            className="flex-1 p-2 border rounded text-sm"
                          />
                          <button
                            type="button"
                            onClick={() => removeSubtask(subtask.id)}
                            className="p-2 text-red-500 hover:bg-red-50 rounded"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              
              <div className="mt-6 flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowNewTaskModal(false);
                    setNewTask({
                      title: '',
                      description: '',
                      priority: 'important_not_urgent',
                      dueDate: '',
                      dueTime: '',
                      scope: 'personal',
                      projectId: '',
                      subtasks: []
                    });
                  }}
                  className="px-4 py-2 bg-gray-200 rounded hover:bg-gray-300"
                >
                  ביטול
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-purple-600 text-white rounded hover:bg-purple-700"
                >
                  צור משימה
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default TeamManagerDashboard;